import { useQuery } from "@tanstack/react-query";
import { Bell, BookOpen, CalendarDays, ChevronRight, Compass, Heart, Home, ListChecks, LockKeyhole, LogOut, MapPin, Menu, Plane, Plus, Search, Users, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../features/auth/auth-context";
import { api } from "../lib/api";
import type { Trip } from "../lib/types";
import { Brand } from "./Brand";
import { NewTripWizard } from "./NewTripWizard";

const nav = [
  { label: "Home", to: "/", icon: Home },
  { label: "Trips", to: "/trips", icon: CalendarDays },
  { label: "Explore", to: "/explore", icon: Compass },
  { label: "Booking Safe", to: "/booking-safe", icon: LockKeyhole },
  { label: "Family", to: "/family", icon: Users },
];

type HeaderPanel = "search" | "notifications" | "profile";
type Reminder = {
  id: string;
  title: string;
  detail: string;
  to: string;
  icon: typeof Bell;
  priority: number;
};

function departureDays(value: string | null) {
  if (!value) return null;
  const start = new Date(`${value}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((start.getTime() - today.getTime()) / 86_400_000);
}

function buildReminders(trips: Trip[]): Reminder[] {
  return trips
    .filter((trip) => !["completed", "cancelled", "archived"].includes(trip.status))
    .flatMap((trip) => {
      const reminders: Reminder[] = [];
      const days = departureDays(trip.start_date);

      if (!trip.destination_provider_id) reminders.push({
        id: `${trip.id}-destination`,
        title: "Verify the destination",
        detail: `${trip.title} needs a confirmed place for accurate weather.`,
        to: `/trips/${trip.id}/overview?settings=1`,
        icon: MapPin,
        priority: 1,
      });
      if (!trip.start_date) reminders.push({
        id: `${trip.id}-dates`,
        title: "Choose travel dates",
        detail: `${trip.title} still has flexible dates.`,
        to: `/trips/${trip.id}/overview?settings=1`,
        icon: CalendarDays,
        priority: 2,
      });
      if (trip.readiness_score < 80) reminders.push({
        id: `${trip.id}-prep`,
        title: "Continue trip preparation",
        detail: `${trip.title} is ${trip.readiness_score}% ready.`,
        to: `/trips/${trip.id}/prep`,
        icon: ListChecks,
        priority: 3,
      });
      if (days !== null && days >= 0 && days <= 30) reminders.push({
        id: `${trip.id}-departure`,
        title: days === 0 ? "Departure is today" : `Departure in ${days} ${days === 1 ? "day" : "days"}`,
        detail: `${trip.title} is coming up. Review the latest plan.`,
        to: `/trips/${trip.id}/overview`,
        icon: Bell,
        priority: 4,
      });

      return reminders;
    })
    .sort((left, right) => left.priority - right.priority)
    .slice(0, 8);
}

export function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const actionsRef = useRef<HTMLDivElement>(null);
  const tripContext = /^\/trips\/[^/]+\//.test(location.pathname);
  const [wizard, setWizard] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [panel, setPanel] = useState<HeaderPanel | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const { data: trips = [], isLoading: tripsLoading, isError: tripsError } = useQuery({
    queryKey: ["trips"],
    queryFn: () => api<Trip[]>("/trips"),
    staleTime: 60_000,
  });
  const reminders = useMemo(() => buildReminders(trips), [trips]);
  const searchResults = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase();
    const newestFirst = [...trips].sort((left, right) => right.created_at.localeCompare(left.created_at));
    if (!query) return newestFirst.slice(0, 5);
    return newestFirst.filter((trip) => [trip.title, trip.destination, trip.destination_name]
      .some((value) => value?.toLocaleLowerCase().includes(query))).slice(0, 8);
  }, [searchQuery, trips]);
  const initials = user?.full_name.split(" ").map((word) => word[0]).slice(0, 2).join("").toUpperCase() || "VO";

  useEffect(() => {
    setPanel(null);
    setMobileNav(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!panel) return;
    const closeOutside = (event: PointerEvent) => {
      if (!actionsRef.current?.contains(event.target as Node)) setPanel(null);
    };
    const closeWithKeyboard = (event: KeyboardEvent) => {
      if (event.key === "Escape") setPanel(null);
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeWithKeyboard);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeWithKeyboard);
    };
  }, [panel]);

  const showPanel = (next: HeaderPanel) => {
    setPanel(next);
    setMobileNav(false);
    if (next === "search") setSearchQuery("");
  };
  const togglePanel = (next: HeaderPanel) => {
    if (panel === next) setPanel(null);
    else showPanel(next);
  };
  const openJourney = (tripId: string) => {
    setPanel(null);
    navigate(`/trips/${tripId}/overview`);
  };

  return <div className={`app-shell${tripContext ? " trip-context" : ""}`}>
    <header className="topbar">
      <NavLink to="/" className="brand-link"><Brand /></NavLink>
      <nav className="primary-nav" aria-label="Primary navigation">
        {nav.map(({ label, to, icon: Icon }) => <NavLink key={to} to={to} end={to === "/"}><Icon size={17} />{label}</NavLink>)}
      </nav>
      <div className="top-actions" ref={actionsRef}>
        <button className={`icon-button search-button${panel === "search" ? " active" : ""}`} aria-label="Search journeys" aria-haspopup="dialog" aria-expanded={panel === "search"} onClick={() => togglePanel("search")}><Search size={19} /></button>
        <button className={`icon-button notification-button${panel === "notifications" ? " active" : ""}`} aria-label={`Travel reminders, ${reminders.length} active`} aria-haspopup="dialog" aria-expanded={panel === "notifications"} onClick={() => togglePanel("notifications")}><Bell size={19} />{reminders.length > 0 && <span className="notification-count">{Math.min(reminders.length, 9)}</span>}</button>
        <button className={`profile-button${panel === "profile" ? " active" : ""}`} onClick={() => togglePanel("profile")} aria-label="Open account menu" aria-haspopup="menu" aria-expanded={panel === "profile"}>{initials}</button>
        <button className="mobile-menu" onClick={() => { setPanel(null); setMobileNav((value) => !value); }} aria-label="Toggle menu" aria-expanded={mobileNav}>{mobileNav ? <X /> : <Menu />}</button>

        {panel === "search" && <section className="header-panel search-panel" role="dialog" aria-label="Search journeys">
          <header><div><p>SEARCH MY TRAVEL</p><h2>Find a journey</h2></div><button onClick={() => setPanel(null)} aria-label="Close search"><X size={17} /></button></header>
          <div className="header-search-field"><Search size={18} /><input autoFocus aria-label="Search journey names or destinations" placeholder="Search journeys or destinations" value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} />{searchQuery && <button aria-label="Clear search" onClick={() => setSearchQuery("")}><X size={15} /></button>}</div>
          <p className="panel-hint">Searches journey names and destinations in your travel library.</p>
          <div className="search-results" aria-live="polite">
            {tripsLoading ? <p className="panel-state">Loading your journeys…</p> : tripsError ? <p className="panel-state error">We couldn’t search your travel library.</p> : searchResults.length ? <>{!searchQuery && <span className="result-label">RECENT JOURNEYS</span>}{searchResults.map((trip) => <button className="search-result" key={trip.id} onClick={() => openJourney(trip.id)}><span><MapPin size={17} /></span><span><strong>{trip.title}</strong><small>{trip.destination_name || trip.destination}</small></span><ChevronRight size={17} /></button>)}</> : <div className="panel-empty"><Search size={22} /><strong>No matching journeys</strong><span>Try a journey name, city, region, or country.</span></div>}
          </div>
          <footer><Link to="/trips" onClick={() => setPanel(null)}>View all journeys<ChevronRight size={15} /></Link></footer>
        </section>}

        {panel === "notifications" && <section className="header-panel notification-panel" role="dialog" aria-label="Travel reminders">
          <header><div><p>TRAVEL REMINDERS</p><h2>{reminders.length ? `${reminders.length} ${reminders.length === 1 ? "item" : "items"} need attention` : "You’re all caught up"}</h2></div><button onClick={() => setPanel(null)} aria-label="Close reminders"><X size={17} /></button></header>
          <div className="reminder-list">
            {tripsLoading ? <p className="panel-state">Checking your plans…</p> : tripsError ? <p className="panel-state error">We couldn’t load your reminders.</p> : reminders.length ? reminders.map(({ id, title, detail, to, icon: Icon }) => <Link key={id} to={to} onClick={() => setPanel(null)}><span className="reminder-icon"><Icon size={17} /></span><span><strong>{title}</strong><small>{detail}</small></span><ChevronRight size={16} /></Link>) : <div className="panel-empty"><Bell size={22} /><strong>No active reminders</strong><span>Your important trip actions will appear here.</span></div>}
          </div>
          <footer><Link to="/trips" onClick={() => setPanel(null)}>Open all journeys<ChevronRight size={15} /></Link></footer>
        </section>}

        {panel === "profile" && <section className="header-panel profile-panel" role="menu" aria-label="Account menu">
          <header className="profile-summary"><span>{initials}</span><div><strong>{user?.full_name}</strong><small>{user?.email}</small></div></header>
          <nav>
            <Link role="menuitem" to="/profile" onClick={() => setPanel(null)}><BookOpen size={17} /><span><strong>Travel profile</strong><small>Preferences and account details</small></span><ChevronRight size={15} /></Link>
            <Link role="menuitem" to="/trips" onClick={() => setPanel(null)}><CalendarDays size={17} /><span><strong>My journeys</strong><small>View and manage every plan</small></span><ChevronRight size={15} /></Link>
            <Link role="menuitem" to="/family" onClick={() => setPanel(null)}><Users size={17} /><span><strong>Family</strong><small>Shared travellers and preferences</small></span><ChevronRight size={15} /></Link>
          </nav>
          <button className="profile-signout" role="menuitem" onClick={() => { setPanel(null); void logout(); }}><LogOut size={17} />Sign out</button>
        </section>}
      </div>
    </header>
    {mobileNav && <nav className="mobile-drawer" aria-label="Mobile navigation">
      <button onClick={() => showPanel("search")}><Search size={18} />Search journeys</button>
      <button onClick={() => showPanel("notifications")}><Bell size={18} />Travel reminders{reminders.length > 0 && <span>{reminders.length}</span>}</button>
      {nav.map(({ label, to, icon: Icon }) => <NavLink key={to} to={to} onClick={() => setMobileNav(false)}><Icon size={18} />{label}</NavLink>)}
      <button onClick={() => { setMobileNav(false); void logout(); }}><LogOut size={18} />Sign out</button>
    </nav>}
    <main><Outlet /></main>
    {!tripContext && <button className="floating-plan" onClick={() => setWizard(true)}><Plus className="desktop-plan-icon" size={19} /><Plane className="mobile-plan-icon" size={19} />Plan a trip</button>}
    <nav className="mobile-tabbar" aria-label="Mobile primary navigation">
      {nav.map(({ label, to, icon: Icon }) => <NavLink key={to} to={to} end={to === "/"}><Icon size={21} /><span>{label === "Booking Safe" ? "Safe" : label}</span></NavLink>)}
    </nav>
    {wizard && <NewTripWizard onClose={() => setWizard(false)} />}
  </div>;
}

export function PlaceholderPage({ kind }: { kind: string }) {
  const content: Record<string, { eyebrow: string; title: string; body: string; icon: typeof Heart }> = {
    Explore: { eyebrow: "YOUR NEXT SOMEDAY", title: "Where will curiosity take you?", body: "Collect destinations, compare seasons, and turn a passing idea into a journey when the time feels right.", icon: Compass },
    "Booking Safe": { eyebrow: "BOOKING SAFE", title: "Every confirmation, exactly where you need it.", body: "Flights, stays, tickets, and travel documents live behind trip-level permissions and private storage.", icon: LockKeyhole },
    Family: { eyebrow: "TRAVEL TOGETHER", title: "One plan, shaped by everyone.", body: "Remember each traveller’s pace and preferences, share decisions, and make room for what matters to the whole family.", icon: Users },
    Trips: { eyebrow: "MY TRAVEL", title: "All your journeys, across time.", body: "Your active, upcoming, past, draft, and dream trips are organized here.", icon: CalendarDays },
    Profile: { eyebrow: "YOUR TRAVEL PROFILE", title: "Preferences that travel with you.", body: "Keep your pace, food, hotel, and transport preferences ready for every new journey.", icon: BookOpen },
  };
  const item = content[kind];
  const Icon = item.icon;
  return <section className="placeholder-page page-width"><div className="placeholder-art"><Icon size={42} strokeWidth={1.25} /></div><p className="eyebrow">{item.eyebrow}</p><h1>{item.title}</h1><p>{item.body}</p><button className="button primary">Begin exploring</button></section>;
}
